/**
 * Collecting a card with Stripe's Payment Element (mounted in `CardFields` through `secure`): Stripe
 * draws and validates the card, expiry, security code, country and postcode itself. Saving creates a
 * SetupIntent, confirms it with Stripe.js — the bank may ask the person to approve the card there and
 * then, in Stripe's own pop-up — and hands the portal the PaymentMethod id. Without Stripe (no keys
 * set) no card can be added; `CardFields` says so.
 */
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { portal } from "@/lib/api";
import type { CardErrors } from "@/lib/payments";
import {
  SETUP_OPTIONS,
  loadStripe,
  paymentElementOptions,
  type StripeElements,
  type StripeJs,
  type StripePaymentElement,
} from "@/lib/stripe";
import type { PaymentMethod } from "@/lib/types";

import type { CardForm } from "./CardFields";

export interface CardEntry {
  /** "stripe" | "simulated" | "none" (no processor) | "loading" */
  provider: string;
  /** Where Stripe mounts its form (null when Stripe isn't the processor). */
  secure: ((node: HTMLDivElement | null) => (() => void) | undefined) | null;
  /** Stripe's form has drawn. */
  ready: boolean;
  /** Field errors before saving (empty when the card looks complete). Stripe highlights its own fields. */
  validate: (card: CardForm) => Promise<CardErrors>;
  /** Moves focus into Stripe's own form (e.g. after a blocked submit whose problem is there, not the
   * name field). Does nothing before Stripe's form has mounted. */
  focus: () => void;
  /**
   * Hands the card to the processor and keeps it on file — or, with `save: false` (the person chose
   * not to save it), only for the one domain payment in hand. Throws with a message for the person.
   */
  addCard: (card: CardForm, options: { makeDefault: boolean; save?: boolean }) => Promise<PaymentMethod>;
  /** Show Stripe's "saved for future payments" wording only while the person is saving the card. */
  showSaveTerms: (on: boolean) => void;
  loadError: string | null;
}

/** `googlePay: false` where Google Pay has a tab of its own (Add payment method). */
export function useCardEntry(orgId: string, { googlePay = true }: { googlePay?: boolean } = {}): CardEntry {
  const methods = useQuery({
    queryKey: ["payment-methods", orgId],
    queryFn: () => portal.paymentMethods(orgId),
  });
  const provider = methods.data?.provider ?? "loading";
  const key = methods.data?.publishable_key ?? null;
  const [stripe, setStripe] = useState<StripeJs | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const element = useRef<StripePaymentElement | null>(null);
  const saveTerms = useRef(true);

  useEffect(() => {
    if (provider !== "stripe" || !key) return;
    let live = true;
    loadStripe(key).then(
      (s) => live && setStripe(s),
      (e: Error) => live && setLoadError(e.message),
    );
    return () => {
      live = false;
    };
  }, [provider, key]);

  const elements = useMemo<StripeElements | null>(
    () => (stripe ? stripe.elements({ ...SETUP_OPTIONS }) : null),
    [stripe],
  );

  // The form lives exactly as long as the node it is mounted in (React 19 ref-callback cleanup).
  const slot = useCallback(
    (node: HTMLDivElement | null) => {
      if (!elements || !node) return;
      const el = elements.create("payment", {
        ...paymentElementOptions({ googlePay }),
        terms: { card: saveTerms.current ? "auto" : "never" },
      });
      el.on("ready", () => setReady(true));
      el.on("loaderror", (e) => setLoadError(e.error.message ?? "Couldn't load the secure card form."));
      el.mount(node);
      element.current = el;
      return () => {
        el.destroy();
        if (element.current === el) element.current = null;
        setReady(false);
      };
    },
    [elements, googlePay],
  );

  const validate = useCallback(
    async (card: CardForm): Promise<CardErrors> => {
      if (provider !== "stripe") return { card: "Card payments aren't switched on yet." };
      if (!elements || !element.current)
        return { card: loadError ?? "The secure card form is still loading." };
      if (!card.name.trim()) return { card: "Enter the name on the card" };
      const { error } = await elements.submit();
      if (!error) return {};
      // Stripe already marks its own fields; an empty message blocks saving without repeating it.
      return { card: error.type === "validation_error" ? "" : (error.message ?? "Check the card details") };
    },
    [provider, elements, loadError],
  );

  const addCard = useCallback(
    async (
      card: CardForm,
      { makeDefault, save = true }: { makeDefault: boolean; save?: boolean },
    ): Promise<PaymentMethod> => {
      const holder = card.name.trim() || undefined;
      if (provider !== "stripe") throw new Error("Card payments aren't switched on yet.");
      if (!stripe || !elements) throw new Error(loadError ?? "The secure card form is still loading.");
      const setup = await portal.preparePaymentMethod(orgId);
      if (!setup.client_secret) throw new Error("Card payments aren't available right now.");
      const result = await stripe.confirmSetup({
        elements,
        clientSecret: setup.client_secret,
        redirect: "if_required",
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: holder ? { billing_details: { name: holder } } : undefined,
        },
      });
      if (result.error || !result.setupIntent)
        throw new Error(result.error?.message ?? "The card was not accepted.");
      if (result.setupIntent.status !== "succeeded")
        throw new Error("Your bank did not approve the card. Try again or use another card.");
      const pm = result.setupIntent.payment_method;
      // Country and postcode come from Stripe's form; the portal reads them off the card.
      return portal.addPaymentMethod(orgId, {
        token: typeof pm === "string" ? pm : pm.id,
        holder_name: holder,
        make_default: save && makeDefault,
        save,
      });
    },
    [provider, stripe, elements, orgId, loadError],
  );

  const showSaveTerms = useCallback((on: boolean) => {
    saveTerms.current = on;
    element.current?.update({ terms: { card: on ? "auto" : "never" } });
  }, []);

  const focus = useCallback(() => {
    element.current?.focus();
  }, []);

  return {
    provider,
    secure: provider === "stripe" ? slot : null,
    ready,
    validate,
    addCard,
    showSaveTerms,
    focus,
    loadError,
  };
}
